import React, { useEffect, useState } from 'react';
import { ScanLine, AlertTriangle, Check, ChevronDown, Zap, Clock, ShieldAlert } from 'lucide-react';
import { globalApi } from '../../services/crmApi';

interface Suggestion {
  field: string;
  label: string;
  currentValue: string | null;
  suggestedValue: string;
  source: string;
  confidence: number;
}

interface ValidationWarning {
  type: 'expired' | 'expiring_soon' | 'pesel_mismatch';
  message: string;
  documentType: string;
  documentName: string;
}

interface Props {
  applicationId: number;
  onFieldsApplied?: () => void;
}

export const OcrSuggestionsPanel: React.FC<Props> = ({ applicationId, onFieldsApplied }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [warnings, setWarnings] = useState<ValidationWarning[]>([]);
  const [ocrCount, setOcrCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    globalApi.getOcrSuggestions(applicationId)
      .then((data: { suggestions: Suggestion[]; validationWarnings: ValidationWarning[]; ocrCount: number }) => {
        if (!mounted) return;
        setSuggestions(data.suggestions || []);
        setWarnings(data.validationWarnings || []);
        setOcrCount(data.ocrCount || 0);
        // Auto-select all empty-field suggestions
        const autoSelect = new Set<string>();
        for (const s of data.suggestions || []) {
          if (!s.currentValue) autoSelect.add(s.field);
        }
        setSelected(autoSelect);
        setLoading(false);
      })
      .catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [applicationId]);

  if (loading || (suggestions.length === 0 && warnings.length === 0)) return null;

  const handleApply = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    try {
      const fields: Record<string, string> = {};
      for (const s of suggestions) {
        if (selected.has(s.field)) fields[s.field] = s.suggestedValue;
      }
      await globalApi.applyOcrSuggestions(applicationId, fields);
      setApplied(true);
      onFieldsApplied?.();
    } catch (err) {
      console.error('OCR apply error:', err);
    } finally {
      setApplying(false);
    }
  };

  const toggleField = (field: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const hasContent = suggestions.length > 0 || warnings.length > 0;

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-cyan-50 rounded-lg">
            <ScanLine className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900">
              Dane z OCR
              {ocrCount > 0 && <span className="ml-2 text-xs text-gray-400">({ocrCount} {ocrCount === 1 ? 'dokument' : 'dokumentów'})</span>}
            </h3>
            <p className="text-xs text-gray-500">
              {suggestions.length > 0 && `${suggestions.length} sugestii`}
              {suggestions.length > 0 && warnings.length > 0 && ' · '}
              {warnings.length > 0 && (
                <span className={warnings.some(w => w.type === 'expired' || w.type === 'pesel_mismatch') ? 'text-red-500 font-medium' : 'text-amber-500'}>
                  {warnings.length} {warnings.length === 1 ? 'ostrzeżenie' : 'ostrzeżeń'}
                </span>
              )}
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && hasContent && (
        <div className="px-4 pb-4 space-y-4">
          {/* Validation warnings */}
          {warnings.length > 0 && (
            <div className="space-y-2">
              {warnings.map((w, i) => (
                <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                  w.type === 'expired' ? 'bg-red-50 text-red-800 border border-red-200' :
                  w.type === 'pesel_mismatch' ? 'bg-orange-50 text-orange-800 border border-orange-200' :
                  'bg-amber-50 text-amber-800 border border-amber-200'
                }`}>
                  {w.type === 'expired' ? <ShieldAlert size={16} className="mt-0.5 flex-shrink-0 text-red-500" /> :
                   w.type === 'pesel_mismatch' ? <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-orange-500" /> :
                   <Clock size={16} className="mt-0.5 flex-shrink-0 text-amber-500" />}
                  <div>
                    <p className="font-medium">{w.message}</p>
                    <p className="text-xs opacity-70 mt-0.5">{w.documentName} ({w.documentType})</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Auto-fill suggestions */}
          {suggestions.length > 0 && !applied && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Wykryte dane z dokumentów — zaznacz co uzupełnić:</p>
              <div className="space-y-1.5">
                {suggestions.map((s) => (
                  <label key={s.field} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(s.field)}
                      onChange={() => toggleField(s.field)}
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">{s.label}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600">{s.confidence}%</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {s.currentValue ? (
                          <><span className="line-through text-red-400">{s.currentValue}</span> → </>
                        ) : (
                          <span className="text-gray-300">(puste)</span>
                        )}
                        {' '}<span className="text-green-600 font-medium">{s.suggestedValue}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <button
                onClick={handleApply}
                disabled={applying || selected.size === 0}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Zap size={14} />
                {applying ? 'Uzupełniam...' : `Uzupełnij ${selected.size} ${selected.size === 1 ? 'pole' : 'pól'} z OCR`}
              </button>
            </div>
          )}

          {applied && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              <Check size={16} />
              <span>Dane zaktualizowane z OCR. Odśwież stronę, by zobaczyć zmiany.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
